import styled from '@emotion/styled';
import {Container, media, spacing, Visible} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {Button} from 'UI/components/action/Button';
import {LinkBasic} from 'UI/components/action/LinkBasic';
import {Menu} from 'UI/components/modules/Header/Menu';

import {useHeader} from './useHeader';
import {Logo, LogoSmall} from 'Images/icons';

import type {Header as HeaderType} from 'types/payload-types';

/**
 * The props for the `Header` component, which renders the website’s top-level header including contact links,
 * logo, and primary navigation.
 * This interface defines the structure expected by the `Header` component. It includes the `headerData` object
 * which supplies configuration for email, phone, navigation, and branding. The optional `testId` enables precise
 * targeting of the component in end-to-end testing environments by applying it as a `data-cy` attribute.
 */
interface ComponentProps {
    /**
     * The configuration and content data required to render the header. This includes navigation items,
     * logo image, and organizational contact details.
     */
    headerData: HeaderType;
    /**
     * Indicates if the current page is the homepage, which may affect layout or metadata rendering.
     */
    isHomepage?: boolean | null;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Header'
     */
    testId?: string;
}

/**
 * A layout component that renders the main site header with top bar contact links and primary navigation menu.
 * This component leverages the `useHeader` hook to extract configuration data such as organizational contact details,
 * logo, and navigation items from the provided `headerData`. It is composed of two main parts: a top bar showing phone
 * and email links, and a menu bar containing the logo and navigation. The component supports test automation through
 * the `testId` prop, which is applied to the root wrapper element as a `data-cy` attribute.
 * It is wrapped with MobX’s `observer` to automatically re-render when observable data changes. It is designed to be
 * responsive and accessible, including ARIA attributes and adaptive logo sizing.
 *
 * @param props            The component props.
 * @param props.headerData The header content and configuration, including contact info, logo, and menu structure.
 * @param props.testId     An optional test ID used for `data-cy` attributes in automated testing. Defaults to `'Header'`.
 * @returns A JSX layout structure for the site’s top header and navigation menu.
 *
 * @example
 * ```tsx
 * <Header headerData={myHeaderData} />
 * ```
 */
const Header = observer(({headerData, isHomepage, testId = 'Header'}: ComponentProps) => {
    const {
        ctaData,
        navigation
    } = useHeader(headerData);

    return isHomepage ? (
        <Wrapper as="header" data-cy={testId} isFluid>
            <StickyWrapper>
                <LinkBasic aria-label="Isarlicht Startseite" href="/">
                    <Visible isLoadingHtml xl xxl>
                        <Logo />
                    </Visible>
                    <Visible isLoadingHtml lg md sm xs>
                        <LogoSmall />
                    </Visible>
                </LinkBasic>
                <Menu ctaData={ctaData} navigation={navigation} />
                <Visible isLoadingHtml xl xxl>
                    <StyledButton
                        as="link"
                        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                        href={ctaData.href!}
                        rel={ctaData.rel}
                        target={ctaData.target}
                    >
                        {ctaData.label}
                    </StyledButton>
                </Visible>
                <Backdrop />
            </StickyWrapper>
        </Wrapper>
    ) : null;
});

Header.displayName = 'Header';

export {Header};

const Wrapper = styled(Container)`
    flex: 0 0 10.4rem;
    padding: ${spacing(4)} ${spacing(4)} ${spacing(4)} ${spacing(4)};
    position: sticky;
    top: 0px;
    z-index: 1000;

    ${media('xl')} {
        flex: 0 0 11.6rem;
        padding-block: unset;
        padding-inline: unset;
    }
`;

const StickyWrapper = styled.div`
    align-items: center;
    border-radius: 200px;
    display: flex;
    justify-content: space-between;
    padding: ${spacing(3)} ${spacing(3)} ${spacing(3)} ${spacing(6)};
    position: relative;

    ${media('xl')} {
        border-radius: none;
        padding: ${spacing(6)} ${spacing(8)} ${spacing(6)} ${spacing(6)};
    }
`;

const StyledButton = styled(Button)`
    outline: 2px solid ${({theme}) => theme.colors.neutral0};
`;

const Backdrop = styled.div`
    backdrop-filter: blur(20px);
    background: hsl(from ${({theme}) => theme.colors.neutral100} h s l / 0.85);
    border: 1px solid hsl(from ${({theme}) => theme.colors.neutral0} h s l / 0.80);
    border-radius: 200px;
    bottom: -1px;
    display: block;
    left: -1px;
    pointer-events: none;
    position: absolute;
    right: -1px;
    top: -1px;
    z-index: -1;

    ${media('xl')} {
        display: none;
    }
`;